// Executive summary: headline numbers (same as the Overview cards) + "What stands out" findings.
import { formatDuration } from '../../live/liveMetrics';
import {
  MARGIN, C, TONE, setFill, setText, pageSize, ensureSpace, toPlain,
  pageHeader, sectionLabel, kpiBox, queueStatus, countStatus, windowLabel, formatOffset,
} from './pdfTheme';

const COUNT_AVG_LOW = 20;
const COUNT_AVG_HIGH = 40;

export const generateExecutiveSummaryPage = (pdf, { summaryMetrics, isCountMode, formatTransactionTime, gameCards, insights, timeRange }) => {
  pdf.addPage();
  const { width } = pageSize(pdf);
  const withData = gameCards.filter((g) => g.hasData);
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

  let y = pageHeader(
    pdf,
    'Executive summary',
    `${windowLabel(timeRange)}: ${formatOffset(timeRange.startOffset)} to ${formatOffset(timeRange.endOffset)} relative to each game's start · ${withData.length} ${withData.length === 1 ? 'game' : 'games'} compared`
  );

  // ---- Headline numbers ----
  const overMinutes = avg(withData.map((g) => g.over.minutes));
  const overShare = avg(withData.map((g) => g.over.share));
  const overflowGames = withData.filter((g) => g.overflow.minutes > 0);
  const totalOverflow = overflowGames.reduce((s, g) => s + g.overflow.minutes, 0);

  const boxes = isCountMode
    ? [
        { label: 'Average count', value: `${summaryMetrics.averagePeopleCount} ppl`, status: countStatus(summaryMetrics.averagePeopleCount, COUNT_AVG_LOW, COUNT_AVG_HIGH), caption: 'Per game, averaged' },
        { label: 'Average peak count', value: `${summaryMetrics.peakAveragePeopleCount} ppl`, status: countStatus(summaryMetrics.peakAveragePeopleCount, COUNT_AVG_LOW, COUNT_AVG_HIGH), caption: 'Typical worst moment' },
        { label: 'Busy time', value: formatDuration(overMinutes), status: overMinutes > 0 ? 'bad' : 'good', caption: `${Math.round(overShare * 100)}% of the window` },
      ]
    : [
        { label: 'Average queue', value: `${summaryMetrics.averageQueueTime} min`, status: queueStatus(summaryMetrics.averageQueueTime), caption: 'Per game, averaged' },
        { label: 'Average peak queue', value: `${summaryMetrics.peakAverageQueueTime} min`, status: queueStatus(summaryMetrics.peakAverageQueueTime), caption: 'Typical worst moment' },
        { label: 'Time over target', value: formatDuration(overMinutes), status: overMinutes > 0 ? 'bad' : 'good', caption: `${Math.round(overShare * 100)}% of the window` },
        { label: 'Overflow', value: totalOverflow > 0 ? formatDuration(totalOverflow) : 'None', status: totalOverflow > 0 ? 'bad' : 'good', caption: totalOverflow > 0 ? `In ${overflowGames.length} of ${withData.length} games` : 'No overflow' },
        { label: 'Average transaction', value: formatTransactionTime(summaryMetrics.averageTransactionTime), status: 'neutral', caption: 'Per game, averaged' },
      ];

  const gap = 3;
  const boxW = (width - MARGIN * 2 - gap * (boxes.length - 1)) / boxes.length;
  const boxH = 28;
  boxes.forEach((b, i) => kpiBox(pdf, MARGIN + i * (boxW + gap), y, boxW, boxH, b));
  y += boxH + 10;

  // ---- What stands out ----
  if (insights.length) {
    y = sectionLabel(pdf, 'What stands out', y);
    insights.forEach((insight, i) => {
      const lead = i === 0;
      const tone = TONE[insight.tone] || TONE.info;
      const textWidth = width - MARGIN * 2 - 12;
      pdf.setFont('helvetica', lead ? 'bold' : 'normal');
      pdf.setFontSize(lead ? 12 : 10);
      const lines = pdf.splitTextToSize(toPlain(insight.text), textWidth);
      const lineH = lead ? 5.6 : 4.8;
      const blockH = 7 + lines.length * lineH + 3;

      y = ensureSpace(pdf, y, blockH + 3);
      if (lead) {
        setFill(pdf, tone.tint);
        pdf.roundedRect(MARGIN, y, width - MARGIN * 2, blockH, 2, 2, 'F');
      }
      setFill(pdf, tone.color);
      pdf.rect(MARGIN, y, 1.4, blockH, 'F');

      setText(pdf, tone.color);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7.5);
      pdf.text(String(insight.label).toUpperCase(), MARGIN + 6, y + 5.5);

      setText(pdf, lead ? C.text : C.muted);
      pdf.setFont('helvetica', lead ? 'bold' : 'normal');
      pdf.setFontSize(lead ? 12 : 10);
      pdf.text(lines, MARGIN + 6, y + 10.5);
      y += blockH + 3;
    });
    y += 4;
  }

  // ---- Definitions ----
  const definitions = isCountMode
    ? [
        'Average count: average people in line across all sections, per game, then averaged across games.',
        'Average peak count: each game\'s highest count (any section), averaged across games.',
        `Busy time: time the average count across sections was above ${COUNT_AVG_HIGH} people, averaged per game.`,
      ]
    : [
        'Average queue: average queue time across all sections, per game, then averaged across games.',
        'Average peak queue: each game\'s longest queue (any section), averaged across games.',
        'Time over target: time the average queue across sections was above 10 min (overflow counts as over), averaged per game.',
        'Overflow: total time a queue was flagged as overflow across the compared games. Values shown with "+" are minimums.',
        'Average transaction: average register transaction time across all sections and games.',
      ];
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  const defLines = definitions.flatMap((d) => pdf.splitTextToSize(`• ${d}`, width - MARGIN * 2));
  y = ensureSpace(pdf, y, defLines.length * 3.8 + 8);
  y = sectionLabel(pdf, 'Definitions', y);
  setText(pdf, C.subtle);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.text(defLines, MARGIN, y);
};
